import { useState } from 'react';
import { Icon } from '@/deploy/components/ui/Icon';
import { Badge } from '@/deploy/components/ui/Badge';
import { Button } from '@/deploy/components/ui/Button';
import { Modal } from '@/deploy/components/ui/Modal';
import { GroupBadge } from '@/deploy/components/ui/GroupBadge';
import { apiServicesService, type ApiService } from '@/deploy/services/api-services.service';
import type { ApiEnvId } from '@/deploy/services/api-pterodactyl-config.service';
import type { ProjectGroup } from '@/deploy/types';
import { ENVIRONMENT_BY_ID } from '@/deploy/types';
import { apiGroupLabel, canonicalApiEnv } from '@/deploy/utils/apiEnvs';
import { NONE_INSTANCE_LABEL, instanceErrorMessage, routedInstance } from '@/deploy/utils/apiInstances';
import { ApiInstanceModal } from './ApiInstanceModal';
import {
  InstanceOptions, NEW_INSTANCE, ROUTING_HELP, RoutingTargetState, isUnknownTarget, routingBody, routingChangeText,
  routingWarning,
} from './ApiRoutingModal';
import pageStyles from '@/deploy/pages/ApiServices.module.scss';
import styles from './ApiInstances.module.scss';

// ============================================================
// Répartition de toutes les API de jeu sur un env : lignes = API, colonnes = groupes de jeu.
// Chaque cellule vaut une instance de l'API, ou « Aucune » (le groupe ne reçoit pas l'API).
// Les modifications sont gardées par cellule (apiId|groupe) le temps de créer une instance ;
// Enregistrer envoie un PUT routing par API modifiée, l'un après l'autre.
// ============================================================

const cellKey = (apiId: string, group: string) => `${apiId}|${group}`;

export function ApiRoutingMatrixModal({ projectId, apis, envId, groups, onClose, onChanged }: {
  projectId: string;
  /** API de jeu d'un CICD qui gère les instances (les externes n'ont pas de répartition). */
  apis: ApiService[];
  envId: ApiEnvId;
  groups: ProjectGroup[];
  onClose: () => void;
  onChanged: () => Promise<void> | void;
}) {
  const env = canonicalApiEnv(envId);
  const envLabel = apiGroupLabel(env);
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [creating, setCreating] = useState<{ apiId: string; group: ProjectGroup } | null>(null);
  const [saving, setSaving] = useState(false);
  const [results, setResults] = useState<Record<string, { ok: boolean; error?: string }>>({});

  const valueOf = (api: ApiService, g: ProjectGroup) => edits[cellKey(api.id, g.key)] ?? routedInstance(api, env, g.key);
  const isChanged = (api: ApiService, g: ProjectGroup) => valueOf(api, g) !== routedInstance(api, env, g.key);
  const changedApis = apis.filter((a) => groups.some((g) => isChanged(a, g)));

  const pick = (api: ApiService, g: ProjectGroup, value: string) => {
    if (value === NEW_INSTANCE) { setCreating({ apiId: api.id, group: g }); return; }
    setEdits((m) => ({ ...m, [cellKey(api.id, g.key)]: value }));
  };

  const save = async () => {
    if (ENVIRONMENT_BY_ID[env].requiresConfirmation) {
      const lines = changedApis.map((a) => `${a.name} : ${groups.filter((g) => isChanged(a, g))
        .map((g) => routingChangeText(a, env, g, valueOf(a, g))).join(', ')}`);
      const warnings = changedApis.flatMap((a) => groups.map((g) => routingWarning(a, valueOf(a, g), env, g)))
        .filter((w): w is string => !!w);
      const text = `Répartition sur ${envLabel} :\n${lines.join('\n')}\n\n`
        + 'Pris en compte au prochain démarrage de chaque serveur de jeu, y compris après un crash.'
        + (warnings.length > 0 ? `\n\n${warnings.join('\n')}` : '')
        + '\n\nContinuer ?';
      if (!confirm(text)) return;
    }
    setSaving(true);
    setResults({});
    const done: string[] = [];
    for (const a of changedApis) {
      try {
        await apiServicesService.setRouting(projectId, a.id, env, routingBody(groups, (g) => valueOf(a, g)));
        setResults((r) => ({ ...r, [a.id]: { ok: true } }));
        done.push(a.id);
      } catch (e) {
        setResults((r) => ({ ...r, [a.id]: { ok: false, error: instanceErrorMessage(e) } }));
      }
    }
    // Les cellules enregistrées reprennent la valeur du CICD, une fois la liste rechargée
    await onChanged();
    setEdits((m) => Object.fromEntries(Object.entries(m).filter(([k]) => !done.includes(k.split('|')[0]))));
    setSaving(false);
  };

  const creatingApi = creating ? apis.find((a) => a.id === creating.apiId) : undefined;
  // Échap ferme d'abord la création d'instance (les deux modales écoutent la touche)
  const close = () => { if (!creating) onClose(); };

  return (
    <>
      <Modal
        open onClose={close}
        title={`Répartition des APIs · ${envLabel}`}
        subtitle="Quelle instance de chaque API sert chaque groupe de jeu"
        width="lg"
        footer={<>
          {changedApis.length > 0 && <Badge tone="info">{changedApis.length} API modifiée{changedApis.length > 1 ? 's' : ''}</Badge>}
          <Button variant="ghost" onClick={onClose} disabled={saving}>Fermer</Button>
          <Button variant="primary" onClick={() => void save()} loading={saving}
            disabled={saving || changedApis.length === 0}>
            Enregistrer
          </Button>
        </>}
      >
        <div className={pageStyles.form}>
          <div className={styles.helpBox}>{ROUTING_HELP}</div>
          {apis.length === 0 ? (
            <div className={pageStyles.empty}>Aucune API de jeu à répartir.</div>
          ) : (
            <div className={styles.matrixWrap}>
              <table className={styles.matrix}>
                <thead>
                  <tr>
                    <th>API</th>
                    {groups.map((g) => <th key={g.key}><GroupBadge group={g} /></th>)}
                  </tr>
                </thead>
                <tbody>
                  {apis.map((a) => {
                    const res = results[a.id];
                    return (
                      <tr key={a.id}>
                        <td className={styles.matrixApi}>
                          {a.name}
                          {res && (
                            <span className={[styles.matrixResult, res.ok ? '' : styles.instNoteDanger].join(' ')}
                              style={res.ok ? { color: 'var(--color-success)' } : undefined}>
                              <Icon name={res.ok ? 'check' : 'x'} size={10} /> {res.ok ? 'Enregistrée' : res.error}
                            </span>
                          )}
                        </td>
                        {groups.map((g) => {
                          const value = valueOf(a, g);
                          return (
                            <td key={g.key} className={isChanged(a, g) ? styles.matrixCellChanged : undefined}>
                              <div className={styles.matrixCell}>
                                <select className={[styles.select, styles.selectSm].join(' ')} value={value}
                                  onChange={(e) => pick(a, g, e.target.value)} disabled={saving}
                                  aria-label={`${a.name} : instance servie au groupe ${g.label}`}>
                                  {isUnknownTarget(a, value) && <option value={value}>{value} (introuvable)</option>}
                                  <InstanceOptions api={a} />
                                </select>
                                <RoutingTargetState api={a} instKey={value} envId={env} group={g} />
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <div className={pageStyles.fieldHint}>
            Une instance sans serveur sur {envLabel} peut être choisie pour préparer la bascule : le groupe ne reçoit
            alors plus l'API. Crée son serveur depuis la ligne de l'API (menu de l'instance, ou « Répartition »).
            « {NONE_INSTANCE_LABEL} » retire l'API au groupe sur {envLabel} : ses placeholders restent non remplacés
            dans les configs de ses serveurs, et ses placeholders qualifiés [[X_HOST@GROUPE]] aussi sur un serveur qui
            sert plusieurs groupes (le proxy).
          </div>
        </div>
      </Modal>

      {creating && creatingApi && (
        <ApiInstanceModal
          projectId={projectId} api={creatingApi} envId={env} mode="create" initialLabel={creating.group.label}
          onClose={() => setCreating(null)} onChanged={onChanged}
          onCreated={(key) => setEdits((m) => ({ ...m, [cellKey(creating.apiId, creating.group.key)]: key }))}
        />
      )}
    </>
  );
}
